import loadable from '@loadable/component';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import { propTypes } from '../../util/types';
import { countUnreadConversations } from '../../util/messageUnread';
import { DEFAULT_MESSAGES_POLL_MS, startSoftPoll } from '../../util/messagePolling';

import { authenticationInProgress, logout } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  changeUserTypeToPractitioner,
  hasCurrentUserErrors,
  sendVerificationEmail,
} from '../../ducks/user.duck';
import { fetchRecentMessages } from '../MessagesPage/MessagesPage.duck';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    unreadMessagesCount,
    onLogout,
    onFetchRecentMessages,
    showListingSection,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onBecomePractitioner,
    onSearchSubmit,
    ...rest
  } = props;

  // Global soft poll for unread badge — Topbar mounts on Landing, Dashboard, etc.
  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    // Mount / auth: always fetch (bypass prior 429 backoff so badge isn't stuck until hard refresh).
    onFetchRecentMessages({ ignoreRateLimit: true });
    return startSoftPoll({
      intervalMs: DEFAULT_MESSAGES_POLL_MS,
      run: () => onFetchRecentMessages(),
      runOnStart: false,
    });
  }, [isAuthenticated, onFetchRecentMessages]);

  return (
    <Topbar
      authInProgress={authInProgress}
      currentPage={currentPage}
      currentSearchParams={currentSearchParams}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      currentUserHasOrders={currentUserHasOrders}
      history={history}
      isAuthenticated={isAuthenticated}
      isLoggedInAs={isLoggedInAs}
      authScopes={authScopes}
      location={location}
      showListingSection={showListingSection}
      notificationCount={notificationCount}
      unreadMessagesCount={unreadMessagesCount}
      onLogout={onLogout}
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      onBecomePractitioner={onBecomePractitioner}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      showGenericError={hasGenericError}
      onSearchSubmit={onSearchSubmit}
      {...rest}
    />
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  unreadMessagesCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  unreadMessagesCount: number,
  onLogout: func.isRequired,
  onFetchRecentMessages: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onBecomePractitioner: func.isRequired,
  showListingSection: bool,
  hasGenericError: bool.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const conversations = state.MessagesPage?.conversations || [];
  const unreadMessagesCount = countUnreadConversations(conversations, currentUser);
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    unreadMessagesCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onBecomePractitioner: () => dispatch(changeUserTypeToPractitioner()),
  onFetchRecentMessages: options => dispatch(fetchRecentMessages(options)),
});

const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
